import { type } from 'arktype';
import type { TimeTrackingPeriodSnapshot } from '../utils/employment-capacity.utils';

export const employeeQuerySchema = type({
  'firstName?': 'string',
  'lastName?': 'string',
  'departmentId?': 'string',
  'jobRoleId?': 'string',
  'teamId?': 'string',
});

export type EmployeeQuery = typeof employeeQuerySchema.t;

export const createEmployeeSchema = type({
  personId: type('string').configure({
    description: 'ID of the person record this employee is derived from.',
    examples: ['person_abc123'],
  }),
  'departmentId?': type('string | null').configure({
    description: 'ID of the department the employee belongs to. `null` clears the assignment.',
    examples: ['department_abc123'],
  }),
  'jobRoleId?': type('string | null').configure({
    description: 'ID of the job role the employee holds. `null` clears the assignment.',
    examples: ['jobrole_abc123'],
  }),
}).configure({ description: 'Payload for promoting an existing person to an employee.' });

export type CreateEmployee = typeof createEmployeeSchema.t;

export const updateEmployeeSchema = type({
  'departmentId?': type('string | null').configure({
    description: 'Updated department ID. `null` clears the assignment.',
    examples: ['department_abc123'],
  }),
  'jobRoleId?': type('string | null').configure({
    description: 'Updated job-role ID. `null` clears the assignment.',
    examples: ['jobrole_abc123'],
  }),
}).configure({ description: 'Partial update of an employee — fields omitted from the body remain unchanged.' });

export type UpdateEmployee = typeof updateEmployeeSchema.t;

/**
 * Skill summary in employee response
 */
export interface EmployeeSkill {
  id: string;
  name: string;
  /** Normalized proficiency rating [0, 1]; null/undefined = held but unrated. */
  rating?: number | null;
  /** ISO date the certification expires, or `null`/undefined if it never expires. */
  expiryDate?: string | null;
}

/**
 * A single employment-active date range. Used to check whether a person was
 * actively employed on a specific date, as distinct from `EmployeeEmployment`
 * (a single derived capacity snapshot for a queried window).
 */
export interface EmployeeActiveEmploymentRange {
  start: string;
  end: string | null;
}

/**
 * Employment capacity data derived from time tracking periods.
 */
export interface EmployeeEmployment {
  workloadHoursPerDay: number;
  targetHoursPerWeek: number;
  employmentRatio: number;
  sourcePeriodId: string | null;
  /** Working-days bitmask (bit 0 = Mon … bit 6 = Sun) from the active period. */
  workdayMask: number;
  /** Number of working days per week derived from the mask (popcount). */
  workingDaysPerWeek: number;
}

/**
 * Floater metadata for an employee who is deployed to another team as a guest.
 * Present when the employee has an approved Labour Pool claim for the current team.
 */
export interface EmployeeFloaterInfo {
  /** The employee's home team ID */
  homeTeamId: string;
  /** The employee's home team name */
  homeTeamName: string;
  /** Colour of the home team (hex or null) */
  homeTeamColor: string | null;
  /** The active deployment date range */
  deploymentRange: { start: string; end: string };
}

/**
 * Deployment entry for an employee who is loaned out to another team.
 * Present when the employee has an approved Labour Pool claim for a different team.
 */
export interface EmployeeDeployment {
  /** The target team ID this employee is deployed to */
  targetTeamId: string;
  /** The target team name */
  targetTeamName: string;
  /** Start date of the deployment (YYYY-MM-DD) */
  start: string;
  /** End date of the deployment (YYYY-MM-DD) */
  end: string;
}

/**
 * Single-day absence occurrence for planning grid rendering.
 * Multi-day and recurring absences are expanded to per-day entries.
 */
export interface EmployeeAbsenceDay {
  /** ISO date (YYYY-MM-DD) for this affected day */
  date: string;
  /** Absence type key, e.g. `absence:vacation` */
  typeKey: string;
  /** Human-readable label shown in planning UI */
  label: string;
  /** Color from source calendar slot when available */
  color: string | null;
  /** Optional timed absence start (HH:mm) */
  startTime?: string;
  /** Optional timed absence end (HH:mm) */
  endTime?: string;
  /** Whether this absence is a full-day absence */
  isAllDay: boolean;
  /** Material icon name from the absence type definition (e.g. `sick`, `beach_access`). */
  icon?: string;
  /**
   * Source calendar slot id — used to deep-link the absence in the calendar app.
   * Always populated by the backend; left optional so legacy callers and test
   * fixtures that only need the display fields don't need to supply it.
   */
  slotId?: string;
  /** Source calendar id, paired with `slotId`. */
  calendarId?: string;
}

/**
 * Employee response from API
 */
export interface Employee {
  id: string;
  personId: string;
  userId?: string | null;
  firstName: string;
  lastName: string;
  email?: string | null;
  profilePicture?: string | null;
  departmentId?: string | null;
  jobRoleId?: string | null;
  availability?: unknown;
  skills?: EmployeeSkill[];
  employment?: EmployeeEmployment;
  /**
   * Present when this employee is a floater in the queried team's context.
   * Populated by the backend when the employee has an active floater person_teams record.
   */
  floaterInfo?: EmployeeFloaterInfo;
  /**
   * Present when this employee is currently deployed as a floater to other teams.
   * Populated by the backend so the lending planner can see "Deployed Out" status.
   */
  deployments?: EmployeeDeployment[];
  /** Time balance summary populated by the API (from time_balance_accounts). Values in hours. */
  balances?: {
    overtimeHours: number;
    vacationHours: number;
    flexitimeHours: number;
  };
  /** Personal absences expanded to per-day entries within requested range. */
  absences?: EmployeeAbsenceDay[];
  /** Every employment period overlapping the queried date window (see {@link EmployeeActiveEmploymentRange}). */
  employmentActiveRanges?: EmployeeActiveEmploymentRange[];
  /**
   * Every employment period overlapping the queried window, with hours
   * already resolved from the dual-field workloadHoursPerDay/workloadPercent
   * model (ADR-0013). Lets callers resolve the correct weekly-hour target
   * for a SPECIFIC week (via `getEmploymentCapacityWithFallback` /
   * `sumTargetHoursAcrossWeeks` from employment-capacity.utils) instead of
   * trusting `employment`'s single snapshot, which only reflects the period
   * covering the start of the queried range — wrong for other weeks when a
   * rate changes mid-range. See per-week-employment-capacity-resolution.md.
   */
  employmentPeriods?: TimeTrackingPeriodSnapshot[];
  /** Tenant full-time hours/week setting, needed alongside `employmentPeriods` to resolve weekly targets. */
  fullTimeHoursPerWeek?: number;
  /** Individual calendar id for this person, when one exists. */
  calendarId?: string | null;
  createdAt: string;
  updatedAt: string;
}
